import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import stripeLogo from "../assets/img/Stripe-Logo-sm.png";
import { usePostHog } from "posthog-js/react";
import { captureAnalyticsEvent } from "../analytics";

type PaymentFormProps = {
  clientSecret: string;
  totalPrice: number;
  currency?: string;
  onPaymentSuccess: () => void;
};

const PaymentForm = ({
  clientSecret,
  totalPrice,
  currency = "USD",
  onPaymentSuccess,
}: PaymentFormProps) => {
  const formattedTotal = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "USD").toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalPrice || 0);
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const posthog = usePostHog();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      console.error("Stripe.js hasn't loaded yet.");
      setLoading(false);
      return;
    }

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement)!,
      },
    });

    if (result.error) {
      alert(result.error.message);
      captureAnalyticsEvent(posthog, "payment_failed", {
        amount: totalPrice,
        errorMessage: result.error.message,
      });
      setLoading(false);
    } else {
      if (result.paymentIntent.status === "succeeded") {
        setSuccess(true);
        setLoading(false);
        onPaymentSuccess();
        captureAnalyticsEvent(posthog, "payment_successful", {
          amount: totalPrice,
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3 text-gray-700">Enter your card details to pay</div>
      <CardElement
        options={{
          style: {
            base: {
              fontSize: "16px",
              color: "#424770",
              "::placeholder": { color: "#aab7c4" },
            },
            invalid: { color: "#9e2146" },
          },
        }}
      />
      <button
        className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!stripe || loading}
      >
        {loading ? "Processing..." : `Pay ${formattedTotal}`}
      </button>
      <div className="mt-3 text-gray-700 flex items-center justify-center text-xs">
        <div>Payment processed and secured by </div>
        <a href="https://stripe.com" target="_blank">
          <img
            src={stripeLogo}
            alt="Stripe Logo"
            className="h-4 ml-1 mt-[0.2em]"
          />
        </a>
      </div>
      {success && <p>Payment successful!</p>}
    </form>
  );
};

export default PaymentForm;
